<template>
    <BaseDialog v-model:open="isDialogOpen" :title="isSaved ? t('calendar.people.saved') : t('calendar.people.added')">
        <h3 v-if="noAttendees" class="title-body text-ink-muted">{{ t("components.calendar.attendeeDialog.noAttendees") }}</h3>
        <div v-else-if="isSaved" class="text-center max-w-[250px]">
            <p class="body-small text-ink-muted mb-5">{{ t("components.calendar.attendeeDialog.peopleSavedBody") }}</p>
            <div class="flex flex-col items-center">
                <BaseLink href="/people" class="base-btn-primary w-fit font-medium rounded-full" data-test-id="my-people-link">{{
                    t("components.calendar.attendeeDialog.myPeople")
                }}</BaseLink>
                <BaseButton theme="tertiary" :class="{ 'font-medium': !tonalThemeEnabled }" @click="closeDialog">{{ t("common.dismiss") }}</BaseButton>
            </div>
        </div>
        <template v-else>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div v-for="attendee in attendees" :key="attendee.connection_id">
                    <ConnectionEditCard
                        :attendee="attendee"
                        @update:title="(title: string) => handleTitleUpdate(title, attendee.connection_id)"
                        @update:dismissed="(dismissed: boolean) => handleDismissedUpdate(dismissed, attendee.connection_id)" />
                </div>
            </div>
        </template>
        <template v-if="!noAttendees && !isSaved" #footer>
            <BasePopover as-child side="top" :side-offset="18" :collision-padding="12">
                <template #trigger>
                    <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('components.calendar.attendeeDialog.aboutPeople')">
                        <i class="bi bi-info-circle" aria-hidden="true"></i>
                    </BaseButton>
                </template>
                <template #content>
                    <div class="body-small max-w-65 p-3">
                        <p class="body-small-bold">{{ t("components.calendar.attendeeDialog.aboutPeople") }}</p>
                        <I18nT keypath="components.calendar.attendeeDialog.aboutPeopleBody" tag="p">
                            <template #private>
                                <span class="font-semibold">{{ t("components.calendar.attendeeDialog.privateToYou") }}</span>
                            </template>
                        </I18nT>
                    </div>
                </template>
            </BasePopover>
            <BaseButton
                data-test-id="save-details-btn"
                theme="primary"
                :size="tonalThemeEnabled ? 'sm' : undefined"
                class="justify-center flex items-center gap-2"
                :class="tonalThemeEnabled ? 'w-50' : 'body-small w-[200px]'"
                :disabled="saveDetailsBtnDisabled"
                @click="handleSaveDetails">
                <LoadingSpinner v-if="isFetching" class="w-4 h-4" />{{
                    isFetching ? t("components.calendar.attendeeDialog.savingDetails") : t("components.calendar.attendeeDialog.saveDetails")
                }}
            </BaseButton>
        </template>
        <BaseToast
            v-model:open="isErrorToastOpen"
            theme="error"
            :description="t('components.calendar.attendeeDialog.saveFailed')"
            data-test-id="save-details-error-toast"></BaseToast>
    </BaseDialog>
</template>

<script setup lang="ts">
type BulkUpdatePayloadItem = { connection_id: string; dismissed?: boolean; job_title?: string };

import { useFetch } from "~vue/api";
import ConnectionEditCard from "~vue/Connections/ConnectionEditCard.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import type { ConnectionCalendarAttendee } from "~vue/types/connection";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, onUnmounted, ref, watch } from "vue";
import { I18nT, useI18n } from "vue-i18n";

import BaseButton from "../BaseButton.vue";
import BaseDialog from "../BaseDialog.vue";
import BasePopover from "../BasePopover.vue";
import BaseToast from "../BaseToast.vue";

const { t } = useI18n();

interface Props {
    attendees?: ConnectionCalendarAttendee[];
}

const props = withDefaults(defineProps<Props>(), {
    attendees: () => [],
});

// TODO: Remove the legacy calendar branch once the tonal theme becomes the default.

const isDialogOpen = defineModel<boolean>("open", { default: false });

const noAttendees = computed(() => props.attendees.length === 0);
const saveDetailsBtnDisabled = computed(() => isFetching.value || bulkUpdatePayload.value.people.length === 0);

const isErrorToastOpen = ref(false);
const bulkUpdatePayload = ref<{ people: BulkUpdatePayloadItem[] }>({ people: [] });

const handleTitleUpdate = (title: string, connectionId: string) => {
    const connection = bulkUpdatePayload.value.people.find((c) => c.connection_id === connectionId);
    if (connection) {
        connection.job_title = title;
    } else {
        bulkUpdatePayload.value.people.push({ connection_id: connectionId, job_title: title });
    }
};

const handleDismissedUpdate = (dismissed: boolean, connectionId: string) => {
    const userInteraction = dismissed ? "connection_dismissed" : "connection_dismissed_undone";
    logUserInteraction(userInteraction, { source: "calendar_integration", connection_id: connectionId });

    const connection = bulkUpdatePayload.value.people.find((c) => c.connection_id === connectionId);
    if (connection) {
        connection["dismissed"] = dismissed;
    } else {
        bulkUpdatePayload.value.people.push({ connection_id: connectionId, dismissed });
    }
};
const { error: bulkUpdateError, execute: bulkUpdate, onFetchError, onFetchResponse, isFetching } = useFetch("/api/connections/bulk/", { immediate: false }).put(bulkUpdatePayload);

onFetchError(() => {
    isErrorToastOpen.value = !isSessionEndedError(bulkUpdateError.value);
});

onFetchResponse(() => {
    isSaved.value = true;
});

const isSaved = ref(false);

const handleSaveDetails = async () => {
    if (bulkUpdatePayload.value.people.length > 0) {
        await bulkUpdate();
    }
};

const closeDialog = () => {
    isDialogOpen.value = false;
};

watch(isDialogOpen, (newVal) => {
    if (!newVal) {
        isSaved.value = false;
        bulkUpdatePayload.value = { people: [] };
    }
});

onUnmounted(() => {
    isDialogOpen.value = false;
});

defineExpose({
    // Exposed for unit testing
    bulkUpdatePayload,
});
</script>
